import { userHasPlatformAdminRole } from "@the-drop/types";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useId, useState } from "react";
import {
  createDispensary,
  type CreateDispensaryRequest,
} from "../lib/dispensary-account-api";
import { fetchMe } from "../lib/fetch-me";
import { writeAdminDispensaryScope } from "../lib/admin-dispensary-scope";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";
import { useWorkspaceMe } from "../workspace/workspace-me-context";

function fieldError(details: unknown, field: string): string | undefined {
  if (!details || typeof details !== "object") return undefined;
  const f = (details as { fieldErrors?: Record<string, string[]> }).fieldErrors;
  if (!f?.[field]?.length) return undefined;
  return f[field]![0];
}

export default function AdminCreateDispensaryScreen() {
  const { getToken } = useDispensaryWorkspace();
  const me = useWorkspaceMe();
  const navigate = useNavigate();
  const formId = useId();

  const [legalName, setLegalName] = useState("");
  const [dbaName, setDbaName] = useState("");
  const [publicSlug, setPublicSlug] = useState("");
  const [clerkOrgId, setClerkOrgId] = useState("");
  const [medLicenseNumber, setMedLicenseNumber] = useState("");
  const [medLicenseType, setMedLicenseType] = useState<CreateDispensaryRequest["medLicenseType"]>("retail");
  const [medLicenseExpiresAt, setMedLicenseExpiresAt] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("CO");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [metrcLicenseNumber, setMetrcLicenseNumber] = useState("");
  const [metrcApiKey, setMetrcApiKey] = useState("");
  const [hasDeliveryPermit, setHasDeliveryPermit] = useState(false);
  const [deliveryPermitNumber, setDeliveryPermitNumber] = useState("");
  const [deliveryPermitExpiresAt, setDeliveryPermitExpiresAt] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [details, setDetails] = useState<unknown>(null);

  if (!me || !userHasPlatformAdminRole(me)) {
    return (
      <div className="max-w-xl rounded-2xl border border-[#EBEBEB] bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,.06)]">
        <h1 className="text-lg font-bold text-[#111111]">Create dispensary</h1>
        <p className="mt-2 text-sm text-[#555555]">Only platform administrators can create dispensaries.</p>
        <Link to="/workspace/dashboard" className="mt-4 inline-block text-sm font-medium text-[#2F5D50]">
          Back to dashboard
        </Link>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setDetails(null);
    setSubmitting(true);
    try {
      const body: CreateDispensaryRequest = {
        legalName: legalName.trim(),
        medLicenseNumber: medLicenseNumber.trim(),
        medLicenseType,
        medLicenseExpiresAt,
        addressLine1: addressLine1.trim(),
        city: city.trim(),
        state: state.trim() || "CO",
        zip: zip.trim(),
        phone: phone.trim(),
        email: email.trim(),
        metrcLicenseNumber: metrcLicenseNumber.trim(),
        metrcApiKey: metrcApiKey,
      };
      if (dbaName.trim()) body.dbaName = dbaName.trim();
      if (publicSlug.trim()) body.publicSlug = publicSlug.trim().toLowerCase();
      if (clerkOrgId.trim()) body.clerkOrgId = clerkOrgId.trim();
      if (addressLine2.trim()) body.addressLine2 = addressLine2.trim();
      body.hasDeliveryPermit = hasDeliveryPermit;
      if (hasDeliveryPermit) {
        body.deliveryPermitNumber = deliveryPermitNumber.trim() || null;
        if (deliveryPermitExpiresAt) body.deliveryPermitExpiresAt = deliveryPermitExpiresAt;
      }

      const result = await createDispensary(getToken, body);
      if (!result.ok) {
        setDetails(result.details);
        if (result.status === 409) {
          if (result.error === "duplicate_med_license_number") {
            setFormError("A dispensary with this MED license number already exists.");
          } else if (result.error === "duplicate_public_slug") {
            setFormError("That public menu slug is already taken.");
          } else {
            setFormError("Conflict — duplicate record.");
          }
        } else if (result.status === 400) {
          setFormError("Check the highlighted fields and try again.");
        } else if (result.status === 403) {
          setFormError("You do not have permission to create dispensaries.");
        } else {
          setFormError("Could not create dispensary. Try again.");
        }
        return;
      }

      writeAdminDispensaryScope(result.dispensary.id);
      await fetchMe(getToken).catch(() => undefined);
      void navigate({ to: "/workspace/dashboard" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">Create dispensary</h1>
        <p className="mt-1 text-sm text-[#555555]">
          Register a licensed retail location with Metrc credentials. Inventory in this platform is driven primarily by
          Metrc webhooks into the API — use sandbox keys for testing (
          <span className="font-mono text-xs">sandbox.metrc.com</span>).
        </p>
      </div>

      {formError ? (
        <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#991B1B]">{formError}</div>
      ) : null}

      <form
        id={formId}
        onSubmit={(e) => void onSubmit(e)}
        className="space-y-8 rounded-2xl border border-[#EBEBEB] bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,.06)]"
      >
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#888888]">Business</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="sm:col-span-2 block text-xs text-[#555555]">
              Legal name *
              <input
                required
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                className="input-field mt-1"
                autoComplete="organization"
              />
              {fieldError(details, "legalName") ? (
                <span className="mt-1 block text-2xs text-[#B91C1C]">{fieldError(details, "legalName")}</span>
              ) : null}
            </label>
            <label className="block text-xs text-[#555555]">
              DBA name
              <input value={dbaName} onChange={(e) => setDbaName(e.target.value)} className="input-field mt-1" />
            </label>
            <label className="block text-xs text-[#555555]">
              Public menu slug
              <input
                value={publicSlug}
                onChange={(e) => setPublicSlug(e.target.value)}
                className="input-field mt-1 font-mono"
                placeholder="e.g. north-denver"
                autoComplete="off"
              />
              {fieldError(details, "publicSlug") ? (
                <span className="mt-1 block text-2xs text-[#B91C1C]">{fieldError(details, "publicSlug")}</span>
              ) : null}
            </label>
            <label className="sm:col-span-2 block text-xs text-[#555555]">
              Clerk organization id (optional)
              <input
                value={clerkOrgId}
                onChange={(e) => setClerkOrgId(e.target.value)}
                className="input-field mt-1 font-mono text-xs"
                placeholder="org_…"
              />
            </label>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#888888]">Colorado MED license</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block text-xs text-[#555555]">
              License number *
              <input
                required
                value={medLicenseNumber}
                onChange={(e) => setMedLicenseNumber(e.target.value)}
                className="input-field mt-1 font-mono"
              />
            </label>
            <label className="block text-xs text-[#555555]">
              License type *
              <select
                required
                value={medLicenseType}
                onChange={(e) => setMedLicenseType(e.target.value as CreateDispensaryRequest["medLicenseType"])}
                className="input-field mt-1"
              >
                <option value="retail">Retail</option>
                <option value="medical">Medical</option>
                <option value="dual">Dual</option>
              </select>
            </label>
            <label className="block text-xs text-[#555555]">
              Expires *
              <input
                required
                type="date"
                value={medLicenseExpiresAt}
                onChange={(e) => setMedLicenseExpiresAt(e.target.value)}
                className="input-field mt-1"
              />
            </label>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#888888]">Metrc (inventory)</h2>
          <p className="mt-1 text-xs text-[#888888]">
            Facility license number and user API key from Metrc (paired with the integrator key on the API). Required for
            outbound Metrc calls and for aligning webhook events with this shop.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block text-xs text-[#555555]">
              Metrc facility license number *
              <input
                required
                value={metrcLicenseNumber}
                onChange={(e) => setMetrcLicenseNumber(e.target.value)}
                className="input-field mt-1 font-mono"
              />
            </label>
            <label className="block text-xs text-[#555555]">
              Metrc user API key *
              <input
                required
                type="password"
                autoComplete="new-password"
                value={metrcApiKey}
                onChange={(e) => setMetrcApiKey(e.target.value)}
                className="input-field mt-1 font-mono"
              />
            </label>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#888888]">Premises & contact</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="sm:col-span-2 block text-xs text-[#555555]">
              Street address *
              <input
                required
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                className="input-field mt-1"
              />
            </label>
            <label className="sm:col-span-2 block text-xs text-[#555555]">
              Address line 2
              <input value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} className="input-field mt-1" />
            </label>
            <label className="block text-xs text-[#555555]">
              City *
              <input required value={city} onChange={(e) => setCity(e.target.value)} className="input-field mt-1" />
            </label>
            <label className="block text-xs text-[#555555]">
              State
              <input value={state} onChange={(e) => setState(e.target.value)} className="input-field mt-1" maxLength={2} />
            </label>
            <label className="block text-xs text-[#555555]">
              ZIP *
              <input required value={zip} onChange={(e) => setZip(e.target.value)} className="input-field mt-1" maxLength={5} />
            </label>
            <label className="block text-xs text-[#555555]">
              Phone *
              <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="input-field mt-1" />
            </label>
            <label className="sm:col-span-2 block text-xs text-[#555555]">
              Email *
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field mt-1" />
            </label>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#888888]">Delivery permit (optional)</h2>
          <label className="mt-3 flex items-center gap-2 text-sm text-[#333333]">
            <input
              type="checkbox"
              checked={hasDeliveryPermit}
              onChange={(e) => setHasDeliveryPermit(e.target.checked)}
            />
            Has delivery permit
          </label>
          {hasDeliveryPermit ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="block text-xs text-[#555555]">
                Permit number
                <input
                  value={deliveryPermitNumber}
                  onChange={(e) => setDeliveryPermitNumber(e.target.value)}
                  className="input-field mt-1"
                />
              </label>
              <label className="block text-xs text-[#555555]">
                Expires
                <input
                  type="date"
                  value={deliveryPermitExpiresAt}
                  onChange={(e) => setDeliveryPermitExpiresAt(e.target.value)}
                  className="input-field mt-1"
                />
              </label>
            </div>
          ) : null}
        </section>

        <div className="flex flex-wrap gap-3 border-t border-[#F0F0F0] pt-6">
          <button type="submit" disabled={submitting} className="btn-primary">
            {submitting ? "Creating…" : "Create dispensary"}
          </button>
          <Link to="/workspace/account" className="btn-secondary inline-flex items-center justify-center">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
